import * as userPaths from '../../users/constants.ts';
import * as customerPaths from '../../customer/constants.ts';
import * as teamPaths from '../../teams/constants.ts';
import * as masterdataPaths from '../../masterdata/constants.ts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useApplicationContext } from '../contexts/ApplicationContext.tsx';
import PageHeader from './Page/internal/PageHeader.tsx';
import PageHeaderTitle from './Page/internal/PageHeaderTitle.tsx';
import { Link, matchPath } from 'react-router-dom';
import SecondaryNavigation from './SecondaryNavigation.tsx';
import {
    canManageAccessControl,
    canManageAgents,
    canManageCustomer,
    canManageIntegrations,
    canManageMasterData,
    canManageTeams,
    canManageUsers,
    hasWorkistPermissions,
} from '../../users/utils.ts';
import { useQuery } from '@apollo/client';
import {
    GET_BUSINESS_CENTRAL_INTEGRATIONS,
    GET_DYNAMICS_FINANCE_OPERATIONS_INTEGRATIONS,
    GET_NETSUITE_INTEGRATIONS,
    GET_QUICKBOOKS_INTEGRATIONS,
    GET_SAGE100_INTEGRATIONS,
    GET_SAGE200PROFESSIONAL_INTEGRATIONS,
    GET_MICROTECH_BUERO_PLUS_INTEGRATIONS,
    GET_SAP_BUSINESS_ONE_INTEGRATIONS,
    GET_SHAREPOINT_INTEGRATIONS,
    GET_WECLAPP_INTEGRATIONS,
} from '../../customer/queries.ts';
import { GET_ORDERWISE_INTEGRATIONS } from '../../customer/pages/Orderwise/queries.ts';
import { GET_ORDAT_INTEGRATIONS } from '../../customer/pages/Ordat/queries.ts';
import { GET_ALPHAPLAN_INTEGRATIONS } from '../../customer/pages/Alphaplan/queries.ts';
import type { IUser } from '../../users/interfaces';

const USER_ITEMS = [
    {
        label: 'components.secondaryNavigation.profile',
        path: userPaths.USER_PROFILE_PATH,
    },
    {
        label: 'components.secondaryNavigation.notifications',
        path: userPaths.USER_NOTIFICATIONS_PATH,
    },
    {
        label: 'components.secondaryNavigation.password',
        path: userPaths.USER_PASSWORD_PATH,
    },
];

const COMPANY_ITEMS = [
    {
        label: 'components.secondaryNavigation.companyProfile',
        path: customerPaths.CUSTOMER_PROFILE_PATH,
        condition: canManageCustomer,
    },
    {
        label: 'components.secondaryNavigation.companyNotifications',
        path: customerPaths.CUSTOMER_NOTIFICATIONS_PATH,
        condition: canManageCustomer,
    },
    {
        label: 'components.secondaryNavigation.companyAgents',
        path: customerPaths.CUSTOMER_AGENTS_PATH,
        condition: canManageCustomer,
    },
    {
        label: 'components.secondaryNavigation.companyDataUsage',
        path: customerPaths.CUSTOMER_DATA_USAGE_PATH,
        condition: canManageCustomer,
    },
    {
        label: 'components.secondaryNavigation.planUsage',
        path: customerPaths.CUSTOMER_PLAN_USAGE_PATH,
        condition: hasWorkistPermissions,
    },
    {
        label: 'components.secondaryNavigation.teams',
        path: teamPaths.TEAMS_PATH,
        condition: canManageTeams,
    },
    {
        label: 'components.secondaryNavigation.employees',
        path: userPaths.USERS_PATH,
        condition: canManageUsers,
    },
    {
        label: 'components.secondaryNavigation.singleSignOn',
        path: customerPaths.CUSTOMER_SSO_PATH,
        condition: canManageAccessControl,
    },
    {
        label: 'components.secondaryNavigation.mfa',
        path: customerPaths.CUSTOMER_MFA_PATH,
        condition: canManageAccessControl,
    },
    {
        label: 'components.secondaryNavigation.saml',
        path: customerPaths.CUSTOMER_SAML_PATH,
        condition: canManageAccessControl,
    },
    {
        label: 'components.secondaryNavigation.masterData',
        path: masterdataPaths.MASTER_DATA_PATH,
        // also highlight on detail/edit pages
        activePaths: [masterdataPaths.MASTER_DATA_PATH, masterdataPaths.MASTER_DATA_DETAIL_PATH],
        condition: canManageMasterData,
    },
    {
        label: 'components.secondaryNavigation.customizations',
        path: customerPaths.CUSTOMER_CUSTOMIZATIONS_PATH,
        condition: hasWorkistPermissions,
    },
];

const INTEGRATION_ITEMS = [
    {
        'components.secondaryNavigation.developerTools': [
            {
                label: 'components.secondaryNavigation.apiIntegrations',
                path: customerPaths.CUSTOMER_API_INTEGRATIONS_PATH,
                condition: canManageIntegrations,
            },
            {
                label: 'components.secondaryNavigation.sftpPublicKeys',
                path: customerPaths.CUSTOMER_INTEGRATION_SFTP_PUBLIC_KEYS_PATH,
                condition: canManageIntegrations,
            },
        ],
    },
    {
        'components.secondaryNavigation.workflowTools': [
            {
                label: 'components.secondaryNavigation.microsoftOutlook365',
                path: customerPaths.CUSTOMER_INTEGRATION_OUTLOOK_PATH,
                condition: canManageIntegrations,
            },
            {
                label: 'components.secondaryNavigation.webhooks',
                path: customerPaths.CUSTOMER_INTEGRATION_WEBHOOK_PATH,
                condition: canManageIntegrations,
            },
        ],
    },
];

const ALL_SETTINGS_ITEMS = [...COMPANY_ITEMS, ...INTEGRATION_ITEMS.flatMap((group) => Object.values(group).flat())];

export const canSeeSettings = (user: IUser) => ALL_SETTINGS_ITEMS.some((item) => item.condition(user));

const getIsActive = (path: string) => {
    return !!matchPath(path, window.location.pathname);
};

const useActiveErpIntegrations = () => {
    const { user } = useApplicationContext();
    // the ERP lists need the integrations permission; SharePoint also accepts the agents permission (knowledge picker)
    const erpOptions = { fetchPolicy: 'cache-and-network' as const, skip: !canManageIntegrations(user) };
    const sharepointOptions = {
        fetchPolicy: 'cache-and-network' as const,
        skip: !canManageAgents(user) && !canManageIntegrations(user),
    };

    const { data: bcData } = useQuery(GET_BUSINESS_CENTRAL_INTEGRATIONS, erpOptions);
    const { data: dfoData } = useQuery(GET_DYNAMICS_FINANCE_OPERATIONS_INTEGRATIONS, erpOptions);
    const { data: sharepointData } = useQuery(GET_SHAREPOINT_INTEGRATIONS, sharepointOptions);
    const { data: weclappData } = useQuery(GET_WECLAPP_INTEGRATIONS, erpOptions);
    const { data: sage100Data } = useQuery(GET_SAGE100_INTEGRATIONS, erpOptions);
    const { data: orderwiseData } = useQuery(GET_ORDERWISE_INTEGRATIONS, erpOptions);
    const { data: ordatData } = useQuery(GET_ORDAT_INTEGRATIONS, erpOptions);
    const { data: alphaplanData } = useQuery(GET_ALPHAPLAN_INTEGRATIONS, erpOptions);
    const { data: sage200Data } = useQuery(GET_SAGE200PROFESSIONAL_INTEGRATIONS, erpOptions);
    const { data: quickbooksData } = useQuery(GET_QUICKBOOKS_INTEGRATIONS, erpOptions);
    const { data: netsuiteData } = useQuery(GET_NETSUITE_INTEGRATIONS, erpOptions);
    const { data: sapData } = useQuery(GET_SAP_BUSINESS_ONE_INTEGRATIONS, erpOptions);
    const { data: microtechData } = useQuery(GET_MICROTECH_BUERO_PLUS_INTEGRATIONS, erpOptions);

    const ALL_ERP_INTEGRATIONS = [
        {
            label: 'components.secondaryNavigation.businessCentral',
            path: customerPaths.CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_PATH,
            count: bcData?.businessCentralIntegrations?.length ?? 0,
        },
        {
            label: 'components.secondaryNavigation.dynamicsFinanceOperations',
            path: customerPaths.CUSTOMER_INTEGRATION_DYNAMICS_FINANCE_OPERATIONS_PATH,
            count: dfoData?.dynamicsFinanceOperationsIntegrations?.length ?? 0,
        },
        {
            label: 'components.secondaryNavigation.sharepoint',
            path: customerPaths.CUSTOMER_INTEGRATION_SHAREPOINT_PATH,
            count: sharepointData?.sharepointIntegrations?.length ?? 0,
        },
        {
            label: 'components.secondaryNavigation.weclapp',
            path: customerPaths.CUSTOMER_INTEGRATION_WECLAPP_PATH,
            count: weclappData?.weclappIntegrations?.length ?? 0,
        },
        {
            label: 'components.secondaryNavigation.sage100',
            path: customerPaths.CUSTOMER_INTEGRATION_SAGE100_PATH,
            count: sage100Data?.sage100Integrations?.length ?? 0,
        },
        {
            label: 'components.secondaryNavigation.orderwise',
            path: customerPaths.CUSTOMER_INTEGRATION_ORDERWISE_PATH,
            count: orderwiseData?.orderwiseIntegrations?.length ?? 0,
        },
        {
            label: 'components.secondaryNavigation.ordat',
            path: customerPaths.CUSTOMER_INTEGRATION_ORDAT_PATH,
            count: ordatData?.ordatIntegrations?.length ?? 0,
        },
        {
            label: 'components.secondaryNavigation.alphaplan',
            path: customerPaths.CUSTOMER_INTEGRATION_ALPHAPLAN_PATH,
            count: alphaplanData?.alphaplanIntegrations?.length ?? 0,
        },
        {
            label: 'components.secondaryNavigation.sage200',
            path: customerPaths.CUSTOMER_INTEGRATION_SAGE200PROFESSIONAL_PATH,
            count: sage200Data?.sage200professionalIntegrations?.length ?? 0,
        },
        {
            label: 'components.secondaryNavigation.quickBooks',
            path: customerPaths.CUSTOMER_INTEGRATION_QUICKBOOKS_PATH,
            count: quickbooksData?.quickbooksIntegrations?.length ?? 0,
        },
        {
            label: 'components.secondaryNavigation.netsuite',
            path: customerPaths.CUSTOMER_INTEGRATION_NETSUITE_PATH,
            count: netsuiteData?.netsuiteIntegrations?.length ?? 0,
        },
        {
            label: 'components.secondaryNavigation.sapbusinessone',
            path: customerPaths.CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_PATH,
            count: sapData?.sapBusinessOneIntegrations?.length ?? 0,
        },
        {
            label: 'components.secondaryNavigation.microtechBueroPlus',
            path: customerPaths.CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_PATH,
            count: microtechData?.microtechBueroPlusIntegrations?.length ?? 0,
        },
    ];

    return ALL_ERP_INTEGRATIONS.filter((integration) => integration.count > 0);
};

const SettingsNavigation = ({ className, ...props }: Omit<React.ComponentPropsWithRef<'nav'>, 'children'>) => {
    const { t } = useTranslation('core');
    const { user } = useApplicationContext();
    const activeErpIntegrations = useActiveErpIntegrations();

    const businessSystemsItems = [
        {
            label: 'components.secondaryNavigation.erpIntegrations',
            path: customerPaths.CUSTOMER_INTEGRATION_ERP_PATH,
        },
        ...activeErpIntegrations,
        {
            label: 'components.secondaryNavigation.customIntegration',
            path: customerPaths.CUSTOMER_INTEGRATION_CUSTOM_PATH,
        },
    ];

    const allIntegrationItems = [
        ...INTEGRATION_ITEMS,
        {
            'components.secondaryNavigation.businessSystems': businessSystemsItems.map((item) => ({
                ...item,
                condition: canManageIntegrations,
            })),
        },
    ];

    return (
        <SecondaryNavigation {...props}>
            <PageHeader className="max-md:hidden" primary={false}>
                <PageHeaderTitle>{t('components.secondaryNavigation.title')}</PageHeaderTitle>
            </PageHeader>

            <SecondaryNavigation.List className="md:overflow-auto">
                <SecondaryNavigation.SectionLabel>
                    {user?.firstName} {user?.lastName}
                </SecondaryNavigation.SectionLabel>

                {USER_ITEMS.map((item) => (
                    <SecondaryNavigation.Button key={item.label} active={getIsActive(item.path)} asChild>
                        <Link to={item.path}>{t(item.label)}</Link>
                    </SecondaryNavigation.Button>
                ))}

                {COMPANY_ITEMS.some((item) => item.condition(user)) && (
                    <>
                        <SecondaryNavigation.SectionLabel>{user?.customer?.name}</SecondaryNavigation.SectionLabel>

                        {COMPANY_ITEMS.filter((item) => item.condition(user)).map((item) => (
                            <SecondaryNavigation.Button
                                key={item.label}
                                active={(item.activePaths ?? [item.path]).some(getIsActive)}
                                asChild
                            >
                                <Link to={item.path}>{t(item.label)}</Link>
                            </SecondaryNavigation.Button>
                        ))}
                    </>
                )}

                {allIntegrationItems.map((group) => {
                    const [categoryName, items] = Object.entries(group)[0] as [
                        string,
                        { label: string; path: string; condition: (user: any) => boolean }[],
                    ];
                    const filteredItems = items.filter((item) => item.condition(user));
                    if (filteredItems.length === 0) return null;
                    return (
                        <React.Fragment key={categoryName}>
                            <SecondaryNavigation.SectionLabel>{t(categoryName)}</SecondaryNavigation.SectionLabel>
                            {filteredItems.map((item) => (
                                <SecondaryNavigation.Button key={item.label} active={getIsActive(item.path)} asChild>
                                    <Link to={item.path}>{t(item.label)}</Link>
                                </SecondaryNavigation.Button>
                            ))}
                        </React.Fragment>
                    );
                })}
            </SecondaryNavigation.List>
        </SecondaryNavigation>
    );
};

export default SettingsNavigation;
